'use client';

import { useEffect, useState } from 'react';

/**
 * Real-data snapshots for the guest homepage, built by
 * `scripts/build_landing_snapshot.py` into `public/landing/`.
 * Every file is small (≈33 KB gzipped together) and fetched after first paint.
 */

export type Meeting = { day: number; start: number; end: number; type: string; where: string; campus: string };

export type SectionSnapshot = {
  code: string;
  title: string;
  credits: string | null;
  section: string;
  index: string;
  instructor: string;
  open: boolean;
  sirs: number | null;
  courseSirs: number | null;
  meetings: Meeting[];
};

export type ScheduleSnapshot = {
  term: string;
  candidates: Array<{ code: string; title: string }>;
  schedule: SectionSnapshot[];
  sniper: SectionSnapshot;
  conflictFreeOptions: number;
  sectionsConsidered: number;
  stats: { courses: number; sections: number; openSections: number; sirsCourses: number };
};

export type BusRoute = { id: string; name: string; short: string; color: string; d: string[]; length: number };

export type BusSnapshot = {
  serviceDate: string;
  capturedAt: string | null;
  view: [number, number];
  projection: {
    west: number;
    north: number;
    cosLat: number;
    scale: number;
    bounds: { south: number; north: number; west: number; east: number };
  };
  routes: BusRoute[];
  stops: Array<{ name: string; x: number; y: number }>;
  buses: Array<{ route: string; name: string; x: number; y: number }>;
};

export type MapPoint = { name: string; x: number; y: number };

export type CampusSnapshot = {
  view: [number, number];
  buildings: string[];
  parking: string[];
  landmarks: MapPoint[];
  campuses: MapPoint[];
};

export type DaySnapshot = {
  dining: { hall: string; meal: string; date: string | null; items: Array<{ station: string; name: string; calories: number | null; tags: string[] }> };
  rooms: { when: string; freeCount: number; totalRooms: number; examples: Array<{ label: string; campus: string; freeUntil: number | null }> };
};

export type LandingEvent = {
  title: string;
  start: string;
  where: string;
  category: string;
  host: string | null;
  image: string | null;
  url: string | null;
};

export type HomeSnapshot = {
  insights: Array<[string, string]>;
  professors: Array<{ name: string; photo: string; rating: number; course: string; code: string }>;
  events: LandingEvent[];
  features: Array<{ name: string; href: string; icon: string }>;
};

type SnapshotMap = {
  bus: BusSnapshot;
  campus: CampusSnapshot;
  schedule: ScheduleSnapshot;
  day: DaySnapshot;
  home: HomeSnapshot;
};

const requests = new Map<keyof SnapshotMap, Promise<unknown>>();

export function loadSnapshot<K extends keyof SnapshotMap>(name: K): Promise<SnapshotMap[K]> {
  let request = requests.get(name);
  if (!request) {
    request = fetch(`/landing/${name}.json`).then((res) => {
      if (!res.ok) throw new Error(`Landing snapshot ${name} failed: ${res.status}`);
      return res.json();
    });
    request.catch(() => requests.delete(name));
    requests.set(name, request);
  }
  return request as Promise<SnapshotMap[K]>;
}

/** Loads several snapshots together; `null` until all are ready or if any fails. */
export function useSnapshots<K extends keyof SnapshotMap>(...names: K[]): { [P in K]: SnapshotMap[P] } | null {
  const [data, setData] = useState<{ [P in K]: SnapshotMap[P] } | null>(null);
  const key = names.join(',');
  useEffect(() => {
    let alive = true;
    Promise.all(names.map((n) => loadSnapshot(n)))
      .then((values) => {
        if (!alive) return;
        setData(Object.fromEntries(names.map((n, i) => [n, values[i]])) as unknown as { [P in K]: SnapshotMap[P] });
      })
      .catch(() => { /* sections without data simply stay hidden */ });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return data;
}

/** Bus lines that only run on weekends or leave New Brunswick are left off the homepage maps. */
export const HIDDEN_ROUTES = new Set(['WKND1', 'WKND2', 'Knightsbridge', 'HLX']);

const NICE_TITLES: Record<string, string> = {
  'Intr Discret Strct I': 'Discrete Structures I',
  'Computer Architectur': 'Computer Architecture',
  'Intro Linear Algebra': 'Linear Algebra',
  'Multivariable Calc': 'Multivariable Calculus',
  'Resrch In Disciplns': 'Research in the Disciplines',
};

/** Catalog titles are truncated to 20 characters; expand the ones the homepage shows. */
export const niceTitle = (title: string) => NICE_TITLES[title] ?? title;

export function formatClock(minutes: number, compact = false) {
  const h = Math.floor(minutes / 60);
  const mm = String(minutes % 60).padStart(2, '0');
  const hour = ((h + 11) % 12) + 1;
  return compact ? `${hour}:${mm}${h < 12 ? 'a' : 'p'}` : `${hour}:${mm} ${h < 12 ? 'AM' : 'PM'}`;
}

/** The app scrolls inside `#main-scroll-container`, not the window. */
export function getScrollRoot(): HTMLElement | null {
  return document.getElementById('main-scroll-container');
}

export function viewportHeight() {
  return getScrollRoot()?.clientHeight || window.innerHeight;
}

export function onScroll(handler: () => void) {
  const root = getScrollRoot();
  let queued = false;
  const listener = () => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => { queued = false; handler(); });
  };
  root?.addEventListener('scroll', listener, { passive: true });
  window.addEventListener('scroll', listener, { passive: true });
  window.addEventListener('resize', listener);
  return () => {
    root?.removeEventListener('scroll', listener);
    window.removeEventListener('scroll', listener);
    window.removeEventListener('resize', listener);
  };
}

export const prefersReducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
